import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import StaffTimeline from './StaffTimeline';
import * as staffService from '../services/staffService';
import usePersistedTab from '../hooks/usePersistedTab';

const {
  FiUser,
  FiFileText,
  FiActivity,
  FiTrendingUp,
  FiMail,
  FiPhone,
  FiMapPin,
  FiRefreshCw,
  FiCheckCircle,
  FiClock,
  FiAlertTriangle
} = FiIcons;

/**
 * Reusable Staff Detail Content Component
 * Used in both modal and dedicated page views
 */
const StaffDetailContent = ({ staff, cases }) => {
  const [activeTab, setActiveTab] = usePersistedTab('overview');
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [performanceMetrics, setPerformanceMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  // Load timeline data when timeline tab is active
  useEffect(() => {
    if (activeTab === 'timeline' && timelineEvents.length === 0) {
      loadTimelineData();
    }
  }, [activeTab]);

  // Load performance metrics when performance tab is active
  useEffect(() => {
    if (activeTab === 'performance' && !performanceMetrics) {
      loadPerformanceMetrics();
    }
  }, [activeTab]);

  const loadTimelineData = async () => {
    try {
      setTimelineLoading(true);
      const timeline = await staffService.getStaffTimeline(staff.id);
      setTimelineEvents(timeline);
    } catch (err) {
      console.error('Error loading staff timeline:', err);
    } finally {
      setTimelineLoading(false);
    }
  };

  const loadPerformanceMetrics = async () => {
    try {
      setMetricsLoading(true);
      const metrics = await staffService.getStaffPerformanceMetrics(staff.id);
      setPerformanceMetrics(metrics);
    } catch (err) {
      console.error('Error loading performance metrics:', err);
    } finally {
      setMetricsLoading(false);
    }
  };

  const handleRecalculateMetrics = async () => {
    try {
      setRecalculating(true);
      await staffService.recalculateStaffMetrics(staff.id);
      // Reload metrics after recalculation
      await loadPerformanceMetrics();
      // Reload staff data to get updated rating
      window.location.reload(); // Simple reload for now
    } catch (err) {
      console.error('Error recalculating metrics:', err);
      alert('Failed to recalculate metrics. Please try again.');
    } finally {
      setRecalculating(false);
    }
  };

  const stats = {
    totalCases: cases.length,
    activeCases: cases.filter(c => c.status === 'active').length,
    completedCases: cases.filter(c => c.status === 'completed').length,
    highPriorityCases: cases.filter(c => c.priority === 'high').length
  };

  const getRiskLevelColor = level => {
    switch (level) {
      case 'high':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low':
        return 'text-green-600 bg-green-50 border-green-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getCaseStatusColor = status => {
    switch (status) {
      case 'active':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'on-hold':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = priority => {
    switch (priority) {
      case 'high':
        return 'text-red-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center space-x-4 mb-6">
        <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-2xl">
          {staff.full_name
            .split(' ')
            .map(n => n[0])
            .join('')}
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{staff.full_name}</h2>
          <p className="text-gray-600">
            {staff.position} • {staff.department}
          </p>
          <p className="text-sm text-gray-500">{staff.employee_id}</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="text-center p-3 bg-blue-50 rounded-lg">
          <p className="text-2xl font-bold text-blue-600">{stats.totalCases}</p>
          <p className="text-sm text-blue-500">Total Cases</p>
        </div>
        <div className="text-center p-3 bg-orange-50 rounded-lg">
          <p className="text-2xl font-bold text-orange-600">{stats.activeCases}</p>
          <p className="text-sm text-orange-500">Active Cases</p>
        </div>
        <div className="text-center p-3 bg-green-50 rounded-lg">
          <p className="text-2xl font-bold text-green-600">{stats.completedCases}</p>
          <p className="text-sm text-green-500">Completed</p>
        </div>
        <div className="text-center p-3 bg-red-50 rounded-lg">
          <p className="text-2xl font-bold text-red-600">{stats.highPriorityCases}</p>
          <p className="text-sm text-red-500">High Priority</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        {/* Mobile Dropdown */}
        <div className="md:hidden mb-4">
          <label htmlFor="staff-tabs" className="sr-only">Select a tab</label>
          <select
            id="staff-tabs"
            name="staff-tabs"
            className="block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500 py-2 px-3"
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
          >
            {[
              { id: 'overview', name: 'Overview' },
              { id: 'cases', name: 'Cases' },
              { id: 'timeline', name: 'Timeline' },
              { id: 'performance', name: 'Performance' }
            ].map(tab => (
              <option key={tab.id} value={tab.id}>{tab.name}</option>
            ))}
          </select>
        </div>

        {/* Desktop Tabs */}
        <nav className="hidden md:flex space-x-8">
          {[
            { id: 'overview', name: 'Overview', icon: FiUser },
            { id: 'cases', name: 'Cases', icon: FiFileText },
            { id: 'timeline', name: 'Timeline', icon: FiActivity },
            { id: 'performance', name: 'Performance', icon: FiTrendingUp }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
            >
              <SafeIcon icon={tab.icon} />
              <span>{tab.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiMail} className="text-gray-400" />
                  <span className="text-gray-900">{staff.email}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiPhone} className="text-gray-400" />
                  <span className="text-gray-900">{staff.phone}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiMapPin} className="text-gray-400" />
                  <span className="text-gray-900">{staff.location}</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Employment Details</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-500">Start Date:</span>
                  <span className="text-gray-900">{new Date(staff.start_date).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Line Manager:</span>
                  <span className="text-gray-900">{staff.line_manager}</span>
                </div>
                {staff.salary && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Salary:</span>
                    <span className="text-gray-900">£{Number(staff.salary).toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Risk Level:</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium border ${getRiskLevelColor(staff.risk_level)}`}>
                    {staff.risk_level.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">Performance Overview</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-700">Current Rating</span>
                <span className="text-xl font-bold text-gray-900">{staff.performance_rating}/5.0</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <div
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${(staff.performance_rating / 5) * 100}%` }}
                />
              </div>
              {staff.last_review_date && (
                <p className="text-sm text-gray-600">Last review: {new Date(staff.last_review_date).toLocaleDateString()}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'cases' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Employee Cases</h3>
            <span className="text-sm text-gray-500">{cases.length} total cases</span>
          </div>

          {cases.length > 0 ? (
            <div className="space-y-4">
              {cases.map((case_, index) => (
                <div key={case_.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="font-medium text-gray-900">{case_.title}</h4>
                      <p className="text-sm text-gray-600">{case_.case_id} • {case_.case_type}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getCaseStatusColor(case_.status)}`}>
                        {case_.status.toUpperCase()}
                      </span>
                      <span className={`text-xs font-medium ${getPriorityColor(case_.priority)}`}>
                        {case_.priority?.toUpperCase()} PRIORITY
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-3">
                    <div>
                      <span>Created: {new Date(case_.created_at).toLocaleDateString()}</span>
                    </div>
                    <div>
                      <span>Last Updated: {new Date(case_.updated_at).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {case_.progress && (
                    <div className="mb-3">
                      <div className="flex justify-between text-sm mb-1">
                        <span>Progress</span>
                        <span>{case_.progress.completionPercentage || 0}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${case_.progress.completionPercentage || 0}%` }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">{case_.description || 'No description available'}</span>
                    <a href={`#/case/${case_.id}`} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                      View Details →
                    </a>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <SafeIcon icon={FiFileText} className="text-4xl mx-auto mb-2 opacity-50" />
              <p>No cases found for this employee</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'timeline' && (
        <div>
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Staff Timeline</h3>
            <p className="text-sm text-gray-600">
              Chronological view of all events across cases involving {staff.full_name}
            </p>
          </div>

          <StaffTimeline events={timelineEvents} loading={timelineLoading} />
        </div>
      )}

      {activeTab === 'performance' && (
        <div className="space-y-6">
          {/* Header with Recalculate Button */}
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Performance Summary</h3>
              <p className="text-sm text-gray-600">Auto-calculated based on case metrics</p>
            </div>
            <button
              onClick={handleRecalculateMetrics}
              disabled={recalculating}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <SafeIcon icon={FiRefreshCw} className={`mr-2 ${recalculating ? 'animate-spin' : ''}`} />
              {recalculating ? 'Recalculating...' : 'Recalculate'}
            </button>
          </div>

          {/* Overall Rating */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-700 font-medium">Overall Performance Rating</span>
              <span className="text-2xl font-bold text-blue-600">{staff.performance_rating}/5.0</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
              <div
                className="bg-gradient-to-r from-blue-500 to-purple-600 h-4 rounded-full transition-all duration-300"
                style={{ width: `${(staff.performance_rating / 5) * 100}%` }}
              />
            </div>
            <p className="text-xs text-gray-600">
              Automatically calculated from case completion, timeliness, action completion, and quality metrics
            </p>
          </div>

          {/* Detailed Metrics */}
          {metricsLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : performanceMetrics ? (
            <>
              {/* Metric Cards Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <SafeIcon icon={FiCheckCircle} className="text-green-600 w-5 h-5" />
                    <span className="text-2xl font-bold text-gray-900">
                      {performanceMetrics.metrics.completionRate}%
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">Case Completion</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {performanceMetrics.metrics.completedCases} of {performanceMetrics.metrics.totalCases} cases
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <SafeIcon icon={FiActivity} className="text-blue-600 w-5 h-5" />
                    <span className="text-2xl font-bold text-gray-900">
                      {performanceMetrics.metrics.actionCompletionRate}%
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">Action Completion</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {performanceMetrics.metrics.completedActions} of {performanceMetrics.metrics.totalActions} actions
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <SafeIcon icon={FiClock} className="text-orange-600 w-5 h-5" />
                    <span className="text-2xl font-bold text-gray-900">
                      {performanceMetrics.metrics.overdueCases}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">Overdue Cases</p>
                  <p className="text-xs text-gray-500 mt-1">Active &gt; 60 days</p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <SafeIcon icon={FiAlertTriangle} className="text-red-600 w-5 h-5" />
                    <span className="text-2xl font-bold text-gray-900">
                      {performanceMetrics.metrics.pendingLegalActions}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">Legal/ACAS Pending</p>
                  <p className="text-xs text-gray-500 mt-1">Critical actions</p>
                </div>
              </div>

              {/* Risk Level Indicator */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Tribunal Risk Level</h4>
                    <p className="text-sm text-gray-600">Likelihood of cases escalating to employment tribunal</p>
                  </div>
                  <span className={`px-4 py-2 rounded-lg text-sm font-bold border-2 ${getRiskLevelColor(staff.risk_level)}`}>
                    {staff.risk_level.toUpperCase()} RISK
                  </span>
                </div>
                <div className="mt-4 text-sm text-gray-600">
                  <p className="mb-2"><strong>Tribunal risk factors:</strong></p>
                  <ul className="space-y-1 ml-4">
                    <li>• High-risk case types (dismissal, discrimination, tribunal-prep)</li>
                    <li>• Medium-risk case types (disciplinary, grievance)</li>
                    <li>• <strong className="text-red-600">Incomplete legal critical actions: {performanceMetrics.metrics.pendingLegalActions}</strong></li>
                    <li>• Long-duration unresolved cases (&gt;90 days)</li>
                    <li>• High-priority unresolved cases: {performanceMetrics.metrics.highPriorityCases}</li>
                  </ul>
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                    <p className="text-xs text-yellow-800">
                      <strong>⚠️ Critical:</strong> Incomplete legal/ACAS actions significantly increase tribunal risk.
                      Complete these actions promptly to reduce exposure.
                    </p>
                  </div>
                </div>
              </div>

              {/* Review Information */}
              {staff.last_review_date && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Review Schedule</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Last Review:</p>
                      <p className="font-medium">{new Date(staff.last_review_date).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Next Review Due:</p>
                      <p className="font-medium">
                        {new Date(
                          new Date(staff.last_review_date).setFullYear(
                            new Date(staff.last_review_date).getFullYear() + 1
                          )
                        ).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>Unable to load performance metrics</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StaffDetailContent;
